import firebase, { User } from 'firebase/app'
import 'firebase/firestore'
import 'firebase/functions'
import 'firebase/auth'
import 'firebase/performance'
import 'firebase/analytics'
import 'firebase/storage'
import firebaseConfig from '../firebase.config'
import { OrganizationInterface, PostInterface, OrganizationUserInterface, UserInterface, InvitationInterface, AttachmentInterface, SocialAttachmentInterface, LabelInterface, OrganizationBrandInterface, NotificationInterface, OrganizationPrivateInterface } from '@/types/interfaces'

const DATA_VERSION = 'v1'
const DATA_VERSION_ORG_PRIVATE = 'v1'

// Initialize Firebase and its services
export const firebaseApp        = firebase.initializeApp(firebaseConfig)
export const firebaseDB         = firebaseApp.firestore()
export const firebaseFunctions  = firebaseApp.functions()
export const firebaseAuth       = firebase.auth()
export const firebaseStorage    = firebase.storage()
export const firebaseAnalytics  = firebase.analytics() // enable Analytics

// Enable Performance tracking
firebase.performance()

// Enable Offline Support
firebase.firestore().settings({
  cacheSizeBytes: firebase.firestore.CACHE_SIZE_UNLIMITED
})
firebase.firestore().enablePersistence({synchronizeTabs: true})


// Authentication

// Return a promise about initial authorization load (needed in main.ts) 
export const auth = {
  user: null,
  requiredEmailActionParameters: ['mode', 'oobCode', 'apiKey'],
  availableEmailActionTypes: ['resetPassword'],

  signUp: async (email: string, password: string) : Promise<UserInterface | undefined> => {
    const signupResponse = await firebaseAuth.createUserWithEmailAndPassword(email, password)

    return signupResponse.user != null ? {
      id: signupResponse.user.uid, 
      name: signupResponse.user.displayName || "anonym", 
      email: signupResponse.user.email || "anonym",
      signedIn: true
    } : undefined
  },
  signIn: async (email: string, password: string) : Promise<UserInterface | undefined> => {
    const signinResponse = await firebaseAuth.signInWithEmailAndPassword(email, password)

    return signinResponse.user != null ? {
      id: signinResponse.user.uid, 
      name: signinResponse.user.displayName || "anonym", 
      email: signinResponse.user.email || "anonym",
      signedIn: true
    } : undefined
  },
  signOut: async () : Promise<any> => await firebaseAuth.signOut(),
  getCurrentUser: async () : Promise<any> => {
    // Get current user
    const firebaseUser: User = await new Promise((resolve, reject) => {
      const unsubscribe = firebaseAuth.onAuthStateChanged((user: any) => {
        unsubscribe() // Stop listening for changes (get the user only once)
        resolve(user)
      }, reject)
    })
    // Get most up-to-date token in case it contains new auth details ONLY IF user is logged in
    if(firebaseUser) firebaseUser.getIdToken(true)
    // Build response
    return {
      id: firebaseUser ? firebaseUser.uid : "",
      name: firebaseUser ? firebaseUser.displayName : "anonym",
      email: firebaseUser ? firebaseUser.email : "anonym",
      signedIn: !!firebaseUser
    }
  },
  /**
   * Send an email to the user to reset the password
   * @param email user's email address
   * @returns a promise
   */
  requestPasswordReset: async (email: string) : Promise<any> => {
    try {
      firebaseAuth.languageCode = 'de' // TODO: needs to be internationalized!
      return await firebaseAuth.sendPasswordResetEmail(email)
    } catch (error) {
      // Avoid security breach of user's email via forgot-password form:
      if(error?.code == "auth/user-not-found") return true
      else throw(error)
    }
  },
  /**
   * Verify that the code is in fact a valid password reset code
   * @param code a code generated by Firebase and sent to the user via email to confirm the validity of the password reset request
   * @returns a promise. When failed, ask the user to generate a new link to reset the password
   */
  resetPasswordVerifyCode: async (code: string) : Promise<any> => {
    return await firebaseAuth.verifyPasswordResetCode(code)
  },
  /**
   * Set a new password for the currently authenticated user
   * @param code a code generated by Firebase and sent to the user via email to confirm the validity of the password reset request
   * @param newPassword the user's new password
   * @returns a promise
   */
  setPassword: async (code: string, newPassword: string) : Promise<any> => {
    return await firebaseAuth.confirmPasswordReset(code, newPassword)
  },
  /**
   * Update the password of an already logged in user
   * @param newPassword the user's new password
   * @returns a promise
   */
  updatePassword: async (payload: {oldPassword: string, newPassword: string}) : Promise<any> => {
    if(!firebaseAuth.currentUser) throw({
      code: "auth/update-password-failed",
      message: "ERROR in updatePassword: No user present.",
      originalError: "ERROR in updatePassword: No user present.",
    })

    // Re-authenticate:
    try {
      console.log("login with: ", firebaseAuth.currentUser.email, payload.oldPassword)
      await firebaseAuth.signInWithEmailAndPassword(firebaseAuth.currentUser.email, payload.oldPassword)
    } catch (error) {
      throw({
        code: "auth/signin-failed",
        message: "ERROR in updatePassword: Authentication failed.",
        originalError: error,
      })
    }

    // Update password:
    try {
      return await firebaseAuth.currentUser.updatePassword(payload.newPassword)
    } catch (error) {
      throw({
        code: "auth/update-password-failed",
        message: "ERROR in updatePassword: Update password failed.",
        originalError: error,
      })
    }
  }
}

// Database

export const db = {
  // ============
  // Organization
  // ============
  /**
   * Get readable (non-private) organization details or null (if organization not found)
   * @param organizationId organization id (also the slug)
   * @returns an object with all organization details or null
   */
  getOrganization: async (organizationId: string) : Promise<OrganizationInterface | undefined> => {
    try {
      const organizationSnapshot = await firebaseDB.
        collection('organizations')
        .doc(organizationId)
        .collection('data')
        .doc(DATA_VERSION)
        .get()
      const organization : OrganizationInterface = organizationSnapshot.data()

      // Skip if organization isn't found
      if(!organizationSnapshot.exists || !organization) return undefined
      // Remove deleted entries from labels:
      const labelsWithoutDeleted = Object.fromEntries(
        Object.entries(organization.labels).filter(([k, v]: [string, LabelInterface]) => !v.isDeleted)
      )
      
      return {
        id: organizationId,
        brand: organization.brand,
        static: organization.static,
        labels: labelsWithoutDeleted,
      }
    } catch (error) {
      return undefined
    }
  },
  /**
   * Update an individual organization label
   * @param organizationId organization id (also the slug)
   * @param label the full label object
   * @returns an empty promise
   */
  updateOrganizationLabel: async (organizationId: string, label: LabelInterface) : Promise<any> => {
    // Update only the label with the given ID:
    const updateObj : {[key: string] : LabelInterface} = {}
    updateObj[`labels.${label.id}`] = label

    return await firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection('data')
      .doc(DATA_VERSION)
      .update(updateObj)
  },
  /**
   * Overwrite the organization's labels
   * @param organizationId organization id (also the slug)
   * @param labelsPositions an object where each key is a label ID and value the label's position as number
   * @returns an empty promise
   */
  updateLabelPositions: async (organizationId: string, labelsPositions: {[key: string]: number}) : Promise<OrganizationInterface | undefined> => {
    const updateObj : {[key: string] : number} = {}
    Object.keys(labelsPositions).forEach(key => {
      updateObj[`labels.${key}.position`] = labelsPositions[key]
    })
    
    return await firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection('data')
      .doc(DATA_VERSION)
      .update(updateObj)
  },
  /**
   * Update the organization's brand parameter
   * @param organizationId organization id (also the slug)
   * @param brand the entire brand object with name, color and logo
   * @returns an empty promise
   */
  updateOrganizationBrand: async (organizationId: string, brand: OrganizationBrandInterface) : Promise<OrganizationInterface | undefined> => {
    return await firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection('data')
      .doc(DATA_VERSION)
      .update({
        brand: brand
      })
  },
  // ============
  // Organization: Private data
  // ============
  /**
   * Get private organization data (such as email templates)
   * @param organizationId organization id (also the slug)
   * @returns an object with private organization data
   */
   getOrganizationPrivate: async (organizationId: string) : Promise<OrganizationPrivateInterface | undefined> => {
    try {
      const privateSnapshot = await firebaseDB.
        collection('organizations')
        .doc(organizationId)
        .collection('privateData')
        .doc(DATA_VERSION_ORG_PRIVATE)
        .get()
      const privateData : OrganizationPrivateInterface = privateSnapshot.data()

      // Skip if organization isn't found
      if(!privateSnapshot.exists || !privateData) return undefined
      return {
        emailNotificationTemplate: privateData.emailNotificationTemplate
      }
    } catch (error) {
      return undefined
    }
  },
  /**
   * Update the organization's private data
   * @param organizationId organization id (also the slug)
   * @param privateData the entire private data object
   * @returns an empty promise
   */
  updateOrganizationPrivate: async (organizationId: string, privateData: OrganizationPrivateInterface) : Promise<undefined> => {
    return await firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection('privateData')
      .doc(DATA_VERSION_ORG_PRIVATE)
      .update({
        emailNotificationTemplate: privateData.emailNotificationTemplate
      })
  },
  // ============
  // Posts
  // ============
  /**
   * GET posts and optionally filter them by labelIds
   * @param organizationId organization id (also the slug)
   * @param filter an object of the following potential filters: labelIds (up to 10), releasedAtStart & releasedAtEnd
   * @param limit set the limit of posts to get (default: 10)
   * @param paginatedPostBefore the actual Firebase post doc object to start after (in case of pagination)
   * @returns an array of posts from Firebase or an empty array, plus the last post (for pagination) 
   */
  getPosts: async (
    organizationId: string,
    filter: {
      labelIds?: string[],
      releasedAtStart?: string,
      releasedAtEnd?: string,
    },
    limit = 10,
    paginatedPostBefore?: object,
  ) : Promise<{
    posts: PostInterface[],
    lastPost: object
  }> => {
    // Get posts from Firestore
    let postsSnapshot = firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection('posts')
      .orderBy('releasedAt', 'desc')

    // Optionally filter by releasedAt END:
    if(filter.releasedAtEnd) postsSnapshot = postsSnapshot.where('releasedAt', '<', filter.releasedAtEnd)
    // Optionally filter by releasedAt START:
    if(filter.releasedAtStart) postsSnapshot = postsSnapshot.where('releasedAt', '>', filter.releasedAtStart)
    // Optionally filter by label ID:
    if(filter.labelIds && filter.labelIds.length > 0) postsSnapshot = postsSnapshot.where('labelIds', 'array-contains-any', filter.labelIds)
    // Optionally paginate fter given past post:
    if(paginatedPostBefore) postsSnapshot = postsSnapshot.startAfter(paginatedPostBefore)
    // Limit and GET posts
    postsSnapshot = await postsSnapshot.limit(limit).get()

    // Return posts as array of post objects:
    const posts = !postsSnapshot.empty ? postsSnapshot.docs.map((doc: any): PostInterface => {
      return _buildClientPost(doc.id, doc.data())
    }) : []
    // Set last post for pagination:
    const lastPost = postsSnapshot.docs[postsSnapshot.docs.length-1]

    return {
      posts,
      lastPost
    }
  },
  /**
   * GET a post for a given organization and post id
   * @param organizationId organization id (also the slug)
   * @param postId post id
   * @returns a promise with the entire post.
   */
  getPost: async (organizationId: string, postId: string) : Promise<PostInterface> => {
    const postSnapshot = await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection("posts")
      .doc(postId)
      .get()
    
    return _buildClientPost(postId, postSnapshot.data())
  },
  /**
   * Write a post for an organization in the database
   * @param organizationId organization id (also the slug)
   * @param post post object
   * @returns a promise. The success determines whether the write in the database was successul
   */
  createPost: async (organizationId: string, post: PostInterface) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('posts')
      .doc()
      .set(_buildServerPost(post))
  },
  /**
   * Update an existing post for an organization in the database
   * @param organizationId organization id (also the slug)
   * @param postId post ID
   * @param post post object
   * @returns a promise. The success determines whether the write in the database was successul
   */
  updatePost: async (organizationId: string, postId: string, post: PostInterface) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('posts')
      .doc(postId)
      .set(_buildServerPost(post))
  },
  /**
   * Delete a post from the database
   * @param organizationId organization id (also the slug)
   * @param postId post ID
   * @returns a promise. The success determines whether the write in the database was successul
   */
  deletePost: async (organizationId: string, postId: string) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('posts').doc(postId).delete()
  },
  // ============
  // (Email) Notifications
  // ============
  /**
   * GET notifications for organization
   * @param organizationId organization id (also the slug)
   * @param limit set the limit of notifications to get (default: 10)
   * @param paginatedNotificationBefore the actual Firebase notification doc object to start after (in case of pagination)
   * @returns an array of notifications from Firebase or an empty array, plus the last notification (for pagination) 
   */
  getNotifications: async (
    organizationId: string,
    limit = 10,
    paginatedNotificationBefore?: object,
  ) : Promise<{
    notifications: NotificationInterface[],
    lastNotification: object
  }> => {
    // Get notifications from Firestore
    let notificationsSnapshot = firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection('notifications')
      .orderBy('createdAt', 'desc')

    // Optionally paginate after given past notification:
    if(paginatedNotificationBefore) notificationsSnapshot = notificationsSnapshot.startAfter(paginatedNotificationBefore)
    // Limit and GET notifications
    notificationsSnapshot = await notificationsSnapshot.limit(limit).get()

    // Return notifications as array of notification objects:
    const notifications = !notificationsSnapshot.empty ? notificationsSnapshot.docs.map((doc: any) => {
      const notification = doc.data()
      return {
        id: doc.id,
        createdAt: notification.createdAt?.toDate(),
        body: notification.body,
        senderUser: notification.senderUser,
        error: notification.error,
        status: notification.status,
      } as NotificationInterface
    }) : []

    // Set last notification for pagination:
    const lastNotification = notificationsSnapshot.docs[notificationsSnapshot.docs.length-1]

    return {
      notifications,
      lastNotification
    }
  },
  /**
   * Create an email notification for an organization in the database
   * @param organizationId organization id (also the slug)
   * @param notification post object
   * @param notification notification object
   * @returns a promise. The success determines whether the write in the database was successul
   */
  createNotification: async (organizationId: string, notification: NotificationInterface) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('notifications')
      .doc()
      .set({
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        body: notification.body,
        senderUser: notification.senderUser,
        error: '',
        status: 'pending',
      })
  },
  // ============
  // Users
  // ============
  /**
   * Gets all users
   * @param organizationId organization id (also the slug)
   * @param limit limit of users per page
   * @param paginatedUserBefore Firebase object of the last loaded user (for pagination)
   * @returns an array of users from Firebase or an empty array AND the last loaded user Firebase object (for pagination)
   */
  getOrganizationUsers: async (
    organizationId: string, 
    limit = 20, 
    paginatedUserBefore?: object,
  ) : Promise<{
    users: Array<OrganizationUserInterface>,
    lastUser: object
  }> => {
    // Get posts from Firestore
    let usersSnapshot = firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection("users")

    // Optionally paginate fter given past post:
    if(paginatedUserBefore) usersSnapshot = usersSnapshot.startAfter(paginatedUserBefore)
    // Limit and GET posts
    usersSnapshot = await usersSnapshot.limit(limit).get()

    // Return users as array of user objects:
    const users = !usersSnapshot.empty ? usersSnapshot.docs.map((doc: any) => {
      const user = doc.data()
      return {
        id: doc.id,
        name: user.name,
        email: user.email,
        role: user.role,
        settings: user.settings,
      }
    }) : []

    // Set last post for pagination:
    const lastUser = usersSnapshot.docs[usersSnapshot.docs.length-1]

    return {
      users,
      lastUser
    }
  },
  /**
   * Create a user record in the database
   * @param organizationId organization id (also the slug)
   * @param user organization user or invitation object
   * @returns a promise. The success determines whether the write in the database was successul
   */
  updateUser: async (organizationId: string, user: OrganizationUserInterface) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('users')
      .doc(user.id)
      .update({
        name: user.name,
        email: user.email,
        role: user.role,
        settings: user.settings,
      })
  },
  /**
   * Delete a user record in the database
   * @param organizationId organization id (also the slug)
   * @param userId user ID
   * @returns a promise. The success determines whether the write in the database was successul
   */
  deleteUser: async (organizationId: string, userId: string) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('users').doc(userId).delete()
  },
  /**
   * Get the user details from the organization
   * @param organizationId organization id (also the slug)
   * @param userId user ID
   * @returns a promise. The success determines whether the GET from the database was successul
   */
  getUser: async (organizationId: string, userId: string) : Promise<OrganizationUserInterface> => {
    // TODO: Refactor for proper error handling
    const organizationUserSnapshot = await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('users').doc(userId).get()

    const organizationUser = organizationUserSnapshot.data()

    const defaultSettings = {
      email: {
        allowOrgNotifications: false
      }
    }

    return {
      id: organizationUserSnapshot?.id || "",
      name: organizationUser?.name || "anonym",
      email: organizationUser?.email || "anonym",
      role: organizationUser?.role || "",
      settings: organizationUser?.settings || defaultSettings,
      isAuthorized: !!organizationUser
    }
  },
  listenUntilGetUser: async (organizationId: string, userId: string) : Promise<OrganizationUserInterface> => {
    return new Promise((resolve, reject) => {
      let unsubscribeListener : Function

      // Create a fallback timeout if user creation takes way too long
      const fallbackTimeout = setTimeout(() => {
        unsubscribeListener()
        reject("Custom Timeout: User was not created within 15 seconds.")
      }, 15000)

      // Listen to changes until user exists:
      unsubscribeListener = firebaseDB.collection('organizations')
        .doc(organizationId)
        .collection('users')
        .doc(userId)
        .onSnapshot((organizationUserSnapshot : any) => {
          const organizationUser = organizationUserSnapshot.data()
          
          // Listen for Firebase updates for the given user ID 
          // Only if the user exists, stop listening, clear the fallbacktimeout and resolve the promise
          if(organizationUserSnapshot.exists){
            unsubscribeListener()
            clearTimeout(fallbackTimeout)
            resolve(organizationUser)
          }
        })
    })
  },
  // ============
  // Invitation
  // ============
  /**
   * Gets all invitations
   * @param organizationId organization id (also the slug)
   * @param limit limit of invitations per page
   * @param paginatedUserBefore Firebase object of the last loaded invitation (for pagination)
   * @returns an array of invitation from Firebase or an empty array AND the last loaded invitation Firebase object (for pagination)
   */
  getOrganizationInvitations: async (
    organizationId: string, 
    limit = 20, 
    paginatedInvitationBefore?: object,
  ) : Promise<{
    invitations: Array<InvitationInterface>,
    lastInvitation: object
  }> => {
    // Get posts from Firestore
    let invitationsSnapshot  = firebaseDB
      .collection('organizations')
      .doc(organizationId)
      .collection("invitations")

    // Optionally paginate fter given past post:
    if(paginatedInvitationBefore) invitationsSnapshot = invitationsSnapshot.startAfter(paginatedInvitationBefore)
    // Limit and GET posts
    invitationsSnapshot = await invitationsSnapshot.limit(limit).get()

    // Return invitations as array of invitation objects:
    const invitations = !invitationsSnapshot.empty ? invitationsSnapshot.docs.map((doc: any) => {
      const invitation = doc.data()
      return {
        id: doc.id,
        name: invitation.name,
        email: invitation.email,
        role: invitation.role,
        userId: invitation.userId
      }
    }) : []

    // Set last post for pagination:
    const lastInvitation = invitationsSnapshot.docs[invitationsSnapshot.docs.length-1]

    return {
      invitations,
      lastInvitation
    }
  },
  /**
   * Get a single invitation information
   * @param organizationId organization id (also the slug)
   * @param invitationId invitation ID (also signup key)
   * @returns the invitation object if found or undefined
   */
  getOrganizationInvitation: async (organizationId: string, invitationId: string) : Promise<InvitationInterface | undefined> => {
    const invitationSnapshot = await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('invitations')
      .doc(invitationId)
      .get()
    const invitation = invitationSnapshot.data()

    return invitationSnapshot.exists && invitation ? {
      id: invitationSnapshot.id,
      name: invitation.name,
      email: invitation.email,
      role: invitation.role,
      userId: invitation.userId
    } : undefined
  },
  /**
   * Create an invitation record in the database
   * @param organizationId organization id (also the slug)
   * @param invitation organization user or invitation object
   * @returns a promise. The success determines whether the write in the database was successul
   */
  createInvitation: async (organizationId: string, invitation: InvitationInterface) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('invitations').doc().set({
        name: invitation.name,
        email: invitation.email,
        role: invitation.role,
        userId: null
      })
  },
  /**
   * Update an invitation record in the database
   * @param organizationId organization id (also the slug)
   * @param invitationId invitation ID (also signup key)
   * @param role either 'author', 'reader' or 'admin'
   * @returns a promise. The success determines whether the write in the database was successul
   */
  updateInvitationRole: async (organizationId: string, invitationId: string, role: string) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('invitations').doc(invitationId).update({ role: role })
  },
  /**
   * Add a user ID to an invitation. IMPORTANT: Triggers a callback!
   * @param organizationId organization id (also the slug)
   * @param invitationId invitation ID (also signup key)
   * @param userId Update the user Id of an invitation
   * @returns a promise. The success determines whether the write in the database was successul
   */
  updateInvitationUser: async (
    organizationId: string, 
    invitationId: string, 
    userDraft: InvitationInterface
  ) : Promise<any> => {
    // ❗️ This triggers a backend callback to create a user record (see ligurio-backend for details)
    return await firebaseDB.collection('organizations')
      .doc(organizationId)
      .collection('invitations')
      .doc(invitationId).update({
        userId: userDraft.userId,
        email: userDraft.email,
        name: userDraft.name,
        role: userDraft.role
      })
  },
  /**
   * Delete an invitation record in the database
   * @param organizationId organization id (also the slug)
   * @param invitationId invitation ID (also signup key)
   * @returns a promise. The success determines whether the write in the database was successul
   */
  deleteInvitation: async (organizationId: string, invitationId: string) : Promise<any> => {
    return await firebaseDB.collection('organizations')
      .doc(organizationId).collection('invitations').doc(invitationId).delete()
  }
}

// Storage

export const storage = {
  async upload(path: string, file: File){
    const metaData = {
      cacheControl: 'private,max-age=31536000',
      contentType: file.type
    }
    
    const reference = firebaseStorage.ref().child(path)
    const uploadResponse = await reference.put(file, metaData)
    const publicURL = await reference.getDownloadURL()

    return _buildUploadResponse(uploadResponse, publicURL)
  },
  async delete(path: string){
    const reference = firebaseStorage.ref().child(path)
    return await reference.delete()
  },
  async get(path: string){
    const reference = firebaseStorage.ref().child(path)
    return await reference.getDownloadURL()
  }
}

// Functions

export const functions = {
  async getServerTime(){
    return new Date(
      (await firebaseFunctions.httpsCallable('serverTime')()).data.ISOString
    )
  },
  async creatOrganizationAsSuperAdmin(organizationId: string){
    return await firebaseFunctions.httpsCallable('createOrganization')({id: organizationId})
  }
}

// PRIVATE METHODS

function _buildUploadResponse(uploadResponse: any, publicURL: string){
  return {
    state: uploadResponse.state,
    name: uploadResponse.metadata.name,
    path: uploadResponse.metadata.fullPath,
    publicURL: publicURL,
    contentType: uploadResponse.metadata.contentType,
    size: uploadResponse.metadata.size
  }
}

function _buildClientPost(id: string, post: any){
  return {
    id: id,
    htmlBody: post.htmlBody,
    bodyAttachments: post.bodyAttachments,
    authorUserId: post.authorUserId,
    authorUserName: post.authorUserName,
    createdAt: post.createdAt?.toDate(),
    updatedAt: post.updatedAt?.toDate() || post.createdAt?.toDate(), 
    releasedAt: post.releasedAt?.toDate(),
    labelIds: post.labelIds,
    attachments: post.attachments,
    socialAttachments: post.socialAttachments
  }
}

function _buildServerPost(post: PostInterface){
  return {
    htmlBody: post.htmlBody,
    bodyAttachments: post.bodyAttachments.map((attachment: AttachmentInterface) => {
      return {
        id: attachment.id,
        path: attachment.path,
        type: attachment.type,
        metadata: attachment.metadata
      }
    }),
    authorUserId: post.authorUserId,
    authorUserName: post.authorUserName,
    createdAt: post.createdAt || firebase.firestore.FieldValue.serverTimestamp(), // Only set a new createdAt if it doesn't exist yet
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    releasedAt: new Date(post.releasedAt),
    labelIds: post.labelIds,
    attachments: post.attachments.map((attachment: AttachmentInterface) => {
      return {
        id: attachment.id,
        path: attachment.path,
        preview: attachment.preview,
        type: attachment.type,
        metadata: attachment.metadata,
        subAttachments: attachment.subAttachments ? attachment.subAttachments.map((subAttachment: AttachmentInterface) => {
          return {
            id: subAttachment.id,
            path: subAttachment.path,
            preview: subAttachment.preview,
            type: subAttachment.type,
            metadata: subAttachment.metadata,
          }
        }) : []
      }
    }),
    socialAttachments: post.socialAttachments.map((socialAttachment: SocialAttachmentInterface) => {
      return {
        reference: socialAttachment.reference,
        platform: socialAttachment.platform
      }
    })
  }
}